(() => {
  const CONTAINER_SELECTOR = '[data-gift-checkpoint-container]';
  const ITEMS_HOLDER_SELECTOR = '[data-items-holder]';
  const CELEBRATE_STAGES = new Set([3, 5]);

  let observer;
  let rafId;
  let isFetching = false;
  let lastCelebratedStage = null;

  const getContainer = () => document.querySelector(CONTAINER_SELECTOR);
  const getWrapper = (root = document) => root.querySelector('[data-gift-checkpoint]');
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const getStageValue = (root = document) => {
    const wrapper = getWrapper(root);
    if (!wrapper) return null;
    const value = Number(wrapper.getAttribute('data-stage'));
    return Number.isNaN(value) ? null : value;
  };

  const getConfettiSettings = (root = document) => {
    const wrapper = getWrapper(root);
    const style = (wrapper?.dataset.confettiStyle || 'burst').toLowerCase();
    const durationSecRaw = parseFloat(wrapper?.dataset.confettiDuration || '2');
    const durationSec = Number.isFinite(durationSecRaw) ? durationSecRaw : 2;
    const durationMs = clamp(durationSec, 1, 5) * 1000;
    return {style, durationMs};
  };

  const createScopedConfetti = (root = document) => {
    if (typeof window.confetti !== 'function') return null;
    const wrapper = getWrapper(root);
    if (!wrapper) return null;

    const canvas = document.createElement('canvas');
    canvas.className = 'hc-gift-checkpoint__confetti';
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.position = 'absolute';
    canvas.style.inset = '0';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.pointerEvents = 'none';
    canvas.style.zIndex = '4';

    const setSize = () => {
      const rect = wrapper.getBoundingClientRect();
      const ratio = window.devicePixelRatio || 1;
      const isNarrow = window.innerWidth <= 640;
      const minHeight = isNarrow ? Math.max(rect.height, 220) : rect.height;
      canvas.width = Math.max(1, Math.round(rect.width * ratio));
      canvas.height = Math.max(1, Math.round(minHeight * ratio));
    };

    setSize();
    wrapper.appendChild(canvas);

    const confettiInstance = window.confetti.create(canvas, {
      resize: false,
      useWorker: true,
      disableForReducedMotion: true
    });

    const cleanup = () => {
      window.removeEventListener('resize', setSize);
      window.setTimeout(() => canvas.remove(), 100);
    };

    window.addEventListener('resize', setSize, {passive: true});

    return {fire: confettiInstance, cleanup};
  };

  const launchConfetti = (stage, root = document) => {
    const {style, durationMs} = getConfettiSettings(root);
    const scoped = createScopedConfetti(root);
    if (!scoped) return;

    const isMobileNarrow = window.innerWidth <= 640;
    const mobileBoost = isMobileNarrow ? 1.25 : 1;
    const scaleBoost = isMobileNarrow ? 1.35 : 1;

    const timeouts = [];
    let cleaned = false;

    const schedule = (fn, delay) => {
      const id = window.setTimeout(fn, delay);
      timeouts.push(id);
      return id;
    };

    const clearAll = () => {
      if (cleaned) return;
      cleaned = true;
      timeouts.forEach(window.clearTimeout);
      scoped.cleanup();
    };

    if (style === 'fountain') {
      const endAt = Date.now() + durationMs;
      const pump = () => {
        const now = Date.now();
        const progress = Math.min(1, (durationMs - (endAt - now)) / durationMs);
        scoped.fire({
          particleCount: (12 + Math.round(8 * (1 - progress * 0.4))) * mobileBoost,
          spread: 75 + progress * 20,
          startVelocity: 34 + progress * 10,
          ticks: 180 + progress * 60,
          gravity: 0.78 + progress * 0.12,
          scalar: 0.92 * scaleBoost,
          origin: {x: 0.5, y: 0.8}
        });

        if (now < endAt) {
          schedule(pump, 140);
        }
      };

      pump();
      schedule(clearAll, durationMs + 800);
    } else {
      const boost = (stage === 5 ? 1.15 : 1) * mobileBoost;
      const baseTicks = 200 + Math.round((durationMs - 1000) / 1000 * 60);
      const shotDelays = [0, durationMs * 0.35, durationMs * 0.7];
      const shots = [
        {
          particleCount: Math.round(90 * boost),
          spread: 78,
          startVelocity: 55,
          ticks: baseTicks + 40,
          gravity: 0.92,
          scalar: 0.95 * scaleBoost,
          origin: {x: 0.2, y: 0.18}
        },
        {
          particleCount: Math.round(90 * boost),
          spread: 78,
          startVelocity: 55,
          ticks: baseTicks + 40,
          gravity: 0.92,
          scalar: 0.95 * scaleBoost,
          origin: {x: 0.8, y: 0.18}
        },
        {
          particleCount: Math.round(120 * boost),
          spread: 110,
          startVelocity: 52,
          ticks: baseTicks,
          gravity: 0.86,
          scalar: 0.9 * scaleBoost,
          origin: {x: 0.5, y: 0.1}
        }
      ];

      shots.forEach((shot, idx) => schedule(() => scoped.fire(shot), shotDelays[idx] || 0));
      schedule(clearAll, durationMs + 800);
    }
  };

  const celebrateIfNeeded = (root = document) => {
    const stage = getStageValue(root);
    if (!stage || !CELEBRATE_STAGES.has(stage)) return;
    if (stage === lastCelebratedStage) return;

    lastCelebratedStage = stage;
    launchConfetti(stage, root);
  };

  const getSectionId = () => {
    const container = getContainer();
    if (!container) return null;
    const section = container.closest('[data-section-id]');
    return section ? section.getAttribute('data-section-id') : null;
  };

  const getSectionUrl = () => {
    const sectionId = getSectionId();
    if (!sectionId) return null;
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  };

  const replaceCheckpoint = (html) => {
    const container = getContainer();
    if (!container) return;

    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;

    const fresh = wrapper.querySelector(CONTAINER_SELECTOR);
    if (!fresh) return;

    container.replaceWith(fresh);
    celebrateIfNeeded(document);
  };

  const refreshCheckpoint = () => {
    const url = getSectionUrl();
    const container = getContainer();
    if (!url || !container || isFetching) return;

    isFetching = true;

    fetch(url, {credentials: 'same-origin', cache: 'no-store'})
      .then((response) => {
        if (!response.ok) throw new Error(`Gift checkpoint refresh failed (${response.status})`);
        return response.text();
      })
      .then(replaceCheckpoint)
      .catch((error) => console.error('[hc-gift-checkpoint] refresh error', error))
      .finally(() => {
        isFetching = false;
      });
  };

  const scheduleRefresh = () => {
    if (rafId) return;
    rafId = window.requestAnimationFrame(() => {
      rafId = null;
      refreshCheckpoint();
    });
  };

  const initObserver = () => {
    if (observer || typeof MutationObserver === 'undefined') return;
    const holder = document.querySelector(ITEMS_HOLDER_SELECTOR);
    if (!holder) return;

    observer = new MutationObserver((mutations) => {
      if (mutations.some((m) => m.type === 'childList')) {
        scheduleRefresh();
      }
    });

    observer.observe(holder, {childList: true});
  };

  const init = () => {
    if (!getContainer()) return;
    initObserver();
    celebrateIfNeeded(document);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, {once: true});
  } else {
    init();
  }

  document.addEventListener('theme:cart:refresh', scheduleRefresh);
})();

